<template>
  <div class="sigdef-responses container-fluid">
    <h2 class="mb-4">Significant Deficiency Responses</h2>

    <b-alert v-if="errorMessage" variant="danger" dismissible @dismissed="errorMessage = null">
      {{ errorMessage }}
    </b-alert>

    <!-- Tabs for different views -->
    <b-tabs v-model="activeTab" content-class="mt-3" @input="onTabChange">
      <!-- Tab 1: Unresolved Deficiencies -->
      <b-tab title="Unresolved Deficiencies" active>
        <b-card v-if="notificationCounts" class="mb-3 bg-light">
          <!-- Every tile counts deficiencies, not responses. Colours match the
               status badges on the cards below. -->
          <div class="d-flex justify-content-around flex-wrap">
            <div class="text-center px-2">
              <h4 class="text-danger mb-0">{{ notificationCounts.awaiting_response }}</h4>
              <small class="text-muted">Awaiting Response</small>
            </div>
            <div class="text-center px-2">
              <h4 class="text-warning mb-0">{{ notificationCounts.pending_review }}</h4>
              <small class="text-muted">Pending Review</small>
            </div>
            <div class="text-center px-2">
              <h4 class="text-warning mb-0">{{ notificationCounts.needs_clarification }}</h4>
              <small class="text-muted">Needs Clarification</small>
            </div>
            <div class="text-center px-2">
              <h4 class="text-info mb-0">{{ notificationCounts.active_schedule }}</h4>
              <small class="text-muted">On Active Schedule</small>
              <div v-if="notificationCounts.active_schedule_overdue > 0">
                <small class="text-danger">
                  {{ notificationCounts.active_schedule_overdue }} past due
                </small>
              </div>
            </div>
            <div v-if="notificationCounts.rejected > 0" class="text-center px-2">
              <h4 class="text-danger mb-0">{{ notificationCounts.rejected }}</h4>
              <small class="text-muted">Rejected</small>
            </div>
            <!-- Accepted deficiencies still appear in the list below until the
                 resolution date is entered in SDWIS, so they need a tile --
                 otherwise the counters read 0 above a non-empty list. -->
            <div v-if="notificationCounts.accepted > 0" class="text-center px-2">
              <h4 class="text-success mb-0">{{ notificationCounts.accepted }}</h4>
              <small class="text-muted">Accepted</small>
              <div><small class="text-muted">awaiting closure</small></div>
            </div>
            <div v-if="notificationCounts.other > 0" class="text-center px-2">
              <h4 class="text-secondary mb-0">{{ notificationCounts.other }}</h4>
              <small class="text-muted">Other</small>
            </div>
          </div>
        </b-card>

        <b-card class="mb-3" no-body>
          <b-card-header header-tag="header" class="p-0">
            <b-button block v-b-toggle.sigdef-instructions variant="warning" class="text-left font-weight-bold">
              Important: Documentation required to resolve deficiencies
            </b-button>
          </b-card-header>
          <b-collapse id="sigdef-instructions" v-model="showInstructions">
            <b-card-body>
              <p>These deficiencies are unresolved in our system. To resolve each one:</p>
              <ol>
                <li>Submit an initial response within <strong>30 days</strong> of notification.</li>
                <li>Submit <strong>proof of correction</strong> (photos, invoices, tank inspections, etc.) within <strong>120 days</strong>. A statement of completion alone is not sufficient.</li>
              </ol>
              <p class="mb-1">If corrections will take longer than 120 days, submit an <strong>extension request before the 120-day deadline</strong> that includes:</p>
              <ul class="mb-0">
                <li>Your PWS name and ID: <strong>{{ pws_name }}, {{ pws_id }}</strong></li>
                <li>Proposed plan of action</li>
                <li>Proposed completion date(s) (month/year)</li>
              </ul>
            </b-card-body>
          </b-collapse>
        </b-card>

        <div v-if="loadingUnresolved" class="text-center py-5">
          <b-spinner></b-spinner>
          <p class="mt-2">Loading deficiencies...</p>
        </div>

        <div v-else-if="unresolvedDeficiencies.length === 0" class="text-center py-5">
          <p class="text-muted">No unresolved deficiencies found.</p>
        </div>

        <b-card
          v-else
          v-for="deficiency in unresolvedDeficiencies"
          :key="deficiency.sigdef_id"
          class="mb-3"
        >
          <b-card-title>
            Deficiency {{ deficiency.sigdef_id }}
            <b-badge
              v-if="deficiency.response_status"
              :variant="getStatusVariant(deficiency.response_status)"
              class="ml-2"
            >
              {{ formatStatus(deficiency.response_status) }}
            </b-badge>
            <small v-if="deficiency.response_count > 1" class="text-muted ml-2">
              {{ deficiency.response_count }} responses submitted
            </small>
          </b-card-title>

          <b-row>
            <b-col md="6">
              <p class="mb-1"><strong>PWS ID:</strong> {{ deficiency.pws_id }}</p>
              <p class="mb-1">
                <strong>Category:</strong>
                {{ deficiency.sanitary_srvey_cat }} - {{ deficiency.sanitary_srvey_cat_desc }}
              </p>
              <p class="mb-1">
                <strong>Deficiency Type:</strong> {{ deficiency.description_cv }}
              </p>
              <p class="mb-1">
                <strong>Determination Date:</strong>
                {{ formatDate(deficiency.determination_date) }}
              </p>
              <p class="mb-1" v-if="deficiency.facility_name">
                <strong>Facility:</strong> {{ deficiency.facility_name }}
              </p>
            </b-col>
            <b-col md="6">
              <p class="mb-1" v-if="deficiency.description_txt">
                <strong>Description:</strong> {{ deficiency.description_txt }}
              </p>
              <p class="mb-1" v-if="deficiency.comments">
                <strong>Comments:</strong><br />
                <span class="small">{{ truncateText(deficiency.comments, 200) }}</span>
              </p>
            </b-col>
          </b-row>

          <b-alert
            v-if="deficiency.response_status === 'active_schedule'"
            variant="info"
            show
            class="mt-3 mb-0 py-2 small"
          >
            This deficiency is on an active corrective action schedule. Keep submitting
            proof of correction, progress documentation, or an extension request until
            the deficiency is resolved.
          </b-alert>

          <div class="mt-3">
            <!-- No response yet -->
            <b-button
              v-if="!deficiency.response_id"
              variant="primary"
              size="sm"
              @click="showResponseForm(deficiency)"
              class="mr-2"
            >
              Submit Response
            </b-button>
            <!-- Latest response has not been reviewed yet, so it can still be edited -->
            <b-button
              v-else-if="isResponseEditable(deficiency.response_status)"
              variant="outline-primary"
              size="sm"
              @click="showResponseForm(deficiency, true)"
              class="mr-2"
            >
              Edit Response
            </b-button>
            <!-- Reviewed, and the system still owes us something: file a new
                 response rather than editing the reviewed one -->
            <b-button
              v-else-if="canSubmitFollowUp(deficiency.response_status)"
              :variant="deficiency.response_status === 'needs_clarification' ? 'warning' : 'primary'"
              size="sm"
              @click="showResponseForm(deficiency)"
              class="mr-2"
            >
              {{ followUpButtonLabel(deficiency.response_status) }}
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="viewDeficiencyDetail(deficiency.sigdef_id)"
            >
              {{ deficiency.response_id ? 'View Response History' : 'View Details' }}
            </b-button>
          </div>
        </b-card>
      </b-tab>

      <!-- Tab 2: My Responses -->
      <b-tab title="My Responses">
        <div class="mb-3">
          <b-form-group label="Filter by status:" label-cols="auto" class="mb-0">
            <b-form-select
              v-model="responseStatusFilter"
              :options="statusOptions"
              @change="loadMyResponses"
            ></b-form-select>
          </b-form-group>
        </div>

        <div v-if="loadingResponses" class="text-center py-5">
          <b-spinner></b-spinner>
          <p class="mt-2">Loading responses...</p>
        </div>

        <div v-else-if="myResponses.length === 0" class="text-center py-5">
          <p class="text-muted">No responses found.</p>
        </div>

        <b-card v-else v-for="response in myResponses" :key="response.id" class="mb-3">
          <b-card-title>
            Deficiency {{ response.sigdef_id }}
            <b-badge :variant="getStatusVariant(response.status)" class="ml-2">
              {{ formatStatus(response.status) }}
            </b-badge>
          </b-card-title>

          <p class="mb-1"><strong>PWS ID:</strong> {{ response.pws_id }}</p>
          <p class="mb-1">
            <strong>Submitted:</strong> {{ formatDate(response.submitted_date) }}
          </p>
          <p class="mb-1"><strong>Response:</strong></p>
          <p class="small">{{ truncateText(response.response_text, 200) }}</p>

          <div v-if="response.reviewer_notes" class="mt-2">
            <b-alert variant="info" show>
              <div class="reviewer-note">
                <div class="reviewer-note-label">MSDH review notes</div>
                <p class="reviewer-note-text mb-0">{{ response.reviewer_notes }}</p>
              </div>
            </b-alert>
          </div>

          <b-button
            variant="outline-primary"
            size="sm"
            @click="viewDeficiencyDetail(response.sigdef_id)"
          >
            View Details
          </b-button>
        </b-card>
      </b-tab>

      <!-- Tab 3: Submit Response -->
      <b-tab title="Submit Response" :disabled="!selectedDeficiency">
        <div v-if="selectedDeficiency">
          <b-button variant="link" class="mb-3 p-0" @click="clearSelection">
            &larr; Back to List
          </b-button>
          <SigDefResponseForm
            :deficiency="selectedDeficiency"
            :existingResponse="editingResponse"
            @response-submitted="onResponseSubmitted"
            @preview-file="previewFile"
            @download-file="downloadFile"
            @delete-file="handleDeleteFile"
          />
        </div>
        <div v-else class="text-center py-5">
          <p class="text-muted">Select a deficiency from the Unresolved tab to submit a response.</p>
        </div>
      </b-tab>

      <!-- Tab 4: Detail View -->
      <b-tab title="Detail View" :disabled="!detailDeficiency">
        <div v-if="detailDeficiency">
          <b-button variant="link" class="mb-3 p-0" @click="closeDetail">
            &larr; Back to List
          </b-button>

          <b-card class="mb-3">
            <h5>Deficiency Information</h5>
            <b-row>
              <b-col md="6">
                <p class="mb-1"><strong>ID:</strong> {{ detailDeficiency.sigdef_id }}</p>
                <p class="mb-1"><strong>PWS ID:</strong> {{ detailDeficiency.pws_id }}</p>
                <p class="mb-1">
                  <strong>Category:</strong>
                  {{ detailDeficiency.sanitary_srvey_cat }} - {{ detailDeficiency.sanitary_srvey_cat_desc }}
                </p>
                <p class="mb-1">
                  <strong>Deficiency Type:</strong> {{ detailDeficiency.description_cv }}
                </p>
                <p class="mb-1">
                  <strong>Determination Date:</strong>
                  {{ formatDate(detailDeficiency.determination_date) }}
                </p>
                <p class="mb-1" v-if="detailDeficiency.facility_name">
                  <strong>Facility:</strong> {{ detailDeficiency.facility_name }}
                  <span v-if="detailDeficiency.facility_status">
                    ({{ detailDeficiency.facility_status }})
                  </span>
                </p>
              </b-col>
              <b-col md="6">
                <p class="mb-1" v-if="detailDeficiency.description_txt">
                  <strong>Description:</strong><br />
                  {{ detailDeficiency.description_txt }}
                </p>
                <p class="mb-1" v-if="detailDeficiency.comments">
                  <strong>Comments:</strong><br />
                  {{ detailDeficiency.comments }}
                </p>
              </b-col>
            </b-row>
          </b-card>

          <!-- Scheduled Activities Section -->
          <b-card
            v-if="detailDeficiency.scheduled_activities && detailDeficiency.scheduled_activities.length > 0"
            class="mb-3"
          >
            <h5>Scheduled Activities</h5>
            <b-table
              :items="detailDeficiency.scheduled_activities"
              :fields="activityFields"
              small
              striped
              responsive
            >
              <template #cell(status)="data">
                <b-badge :variant="getActivityStatusVariant(data.value)">
                  {{ data.value }}
                </b-badge>
              </template>
              <template #cell(due_date)="data">
                {{ formatDate(data.value) }}
              </template>
              <template #cell(projected_date)="data">
                {{ data.value ? formatDate(data.value) : '-' }}
              </template>
              <template #cell(achieved_date)="data">
                {{ data.value ? formatDate(data.value) : '-' }}
              </template>
            </b-table>
          </b-card>

          <div class="d-flex justify-content-between align-items-center mt-4 mb-2">
            <h5 class="mb-0">Response History</h5>
            <!-- Same rule as the list cards: no response yet, or the latest one
                 is reviewed and still leaves the system something to do. -->
            <b-button
              v-if="!detailDeficiency.resolved_date
                && (!detailLatestStatus() || canSubmitFollowUp(detailLatestStatus()))"
              variant="primary"
              size="sm"
              @click="showResponseForm(detailDeficiency)"
            >
              {{ followUpButtonLabel(detailLatestStatus()) }}
            </b-button>
          </div>
          <div v-if="detailDeficiency.responses && detailDeficiency.responses.length > 0">
            <b-card
              v-for="resp in detailDeficiency.responses"
              :key="resp.id"
              class="mb-3"
            >
              <b-card-title>
                Response #{{ resp.id }}
                <b-badge :variant="getStatusVariant(resp.status)" class="ml-2">
                  {{ formatStatus(resp.status) }}
                </b-badge>
              </b-card-title>

              <p class="mb-1">
                <strong>Submitted:</strong> {{ formatDate(resp.submitted_date) }}
                by {{ resp.submitted_by_name || resp.submitted_by_email }}
              </p>
              <p class="mb-1"><strong>Response:</strong></p>
              <p>{{ resp.response_text }}</p>

              <div v-if="resp.resolution_description">
                <p class="mb-1"><strong>Resolution:</strong></p>
                <p>{{ resp.resolution_description }}</p>
              </div>

              <p v-if="resp.resolution_date" class="mb-1">
                <strong>Resolution Date:</strong> {{ formatDate(resp.resolution_date) }}
              </p>

              <div v-if="resp.reviewer_notes" class="mt-2">
                <b-alert variant="info" show>
                  <div class="reviewer-note">
                    <div class="reviewer-note-label">MSDH review notes</div>
                    <p class="reviewer-note-text mb-0">{{ resp.reviewer_notes }}</p>
                  </div>
                  <small class="d-block mt-2">
                    Reviewed by {{ resp.reviewed_by }} on {{ formatDate(resp.reviewed_date) }}
                  </small>
                </b-alert>
              </div>

              <div v-if="resp.files && resp.files.length > 0" class="mt-3">
                <h6>Attached Files:</h6>
                <b-list-group>
                  <b-list-group-item
                    v-for="file in resp.files"
                    :key="file.id"
                    class="d-flex justify-content-between align-items-center"
                  >
                    <span>
                      <b-icon :icon="getFileIcon(file)"></b-icon>
                      {{ file.filename }}
                      <small class="text-muted">({{ formatFileSize(file.file_size) }})</small>
                    </span>
                    <div>
                      <b-button
                        v-if="canPreview(file)"
                        variant="outline-primary"
                        size="sm"
                        @click="previewFile(file)"
                        class="mr-2"
                      >
                        <b-icon icon="eye"></b-icon> Preview
                      </b-button>
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        @click="downloadFile(file.id)"
                      >
                        <b-icon icon="download"></b-icon> Download
                      </b-button>
                    </div>
                  </b-list-group-item>
                </b-list-group>
              </div>
            </b-card>
          </div>
          <div v-else class="text-center py-3">
            <p class="text-muted">No responses submitted yet.</p>
          </div>
        </div>
      </b-tab>
    </b-tabs>

    <!-- Preview Modal -->
    <b-modal
      id="file-preview-modal"
      :title="previewTitle"
      size="xl"
      ok-only
      ok-title="Close"
    >
      <div v-if="previewType === 'pdf'">
        <iframe :src="previewUrl" style="width: 100%; height: 80vh; border: none;"></iframe>
      </div>
      <div v-else-if="previewType === 'image'" class="text-center">
        <img :src="previewUrl" style="max-width: 100%; max-height: 80vh;" alt="Preview" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import SigDefResponseForm from '../components/SigDefResponseForm.vue';

export default {
  name: 'SigDefResponses',
  components: {
    SigDefResponseForm,
  },
  data() {
    return {
      pws_id: '',
      pws_name: '',
      activeTab: 0,
      unresolvedDeficiencies: [],
      myResponses: [],
      notificationCounts: null,
      selectedDeficiency: null,
      editingResponse: null,
      detailDeficiency: null,
      responseStatusFilter: null,
      loadingUnresolved: false,
      loadingResponses: false,
      errorMessage: null,
      showInstructions: localStorage.getItem('sigdef_show_instructions') !== 'false',
      statusOptions: [
        { value: null, text: 'All Statuses' },
        { value: 'pending', text: 'Pending' },
        { value: 'under_review', text: 'Under Review' },
        { value: 'accepted', text: 'Accepted' },
        { value: 'active_schedule', text: 'Active Schedule' },
        { value: 'rejected', text: 'Rejected' },
        { value: 'needs_clarification', text: 'Needs Clarification' },
      ],
      previewUrl: null,
      previewType: null,
      previewTitle: '',
      activityFields: [
        { key: 'activity_name', label: 'Activity Type' },
        { key: 'due_date', label: 'Due Date' },
        { key: 'projected_date', label: 'Projected' },
        { key: 'achieved_date', label: 'Completed' },
        { key: 'status', label: 'Status' },
      ],
    };
  },
  mounted() {
    this.pws_id = this.$store.getters.pws_id;
    this.pws_name = this.$store.getters.pws_name;
    this.loadUnresolvedDeficiencies();
    this.loadNotificationCounts();
    this.loadMyResponses();
  },
  watch: {
    showInstructions(val) {
      localStorage.setItem('sigdef_show_instructions', val);
    },
  },
  methods: {
    onTabChange() {
      // Remove focus from any focused element when switching tabs
      // This prevents aria-hidden accessibility warnings
      if (document.activeElement) {
        document.activeElement.blur();
      }
      // Scroll to top of page when switching tabs
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    async loadUnresolvedDeficiencies() {
      this.loadingUnresolved = true;
      try {
        const params = {};
        if (this.pws_id) {
          params.pws_id = this.pws_id;
        }
        const response = await axios.get('/api/sigdefs/unresolved', { params });
        this.unresolvedDeficiencies = response.data;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading unresolved deficiencies:', error);
        this.errorMessage = 'Failed to load deficiencies';
      } finally {
        this.loadingUnresolved = false;
      }
    },
    async loadMyResponses() {
      this.loadingResponses = true;
      try {
        const params = {};
        if (this.pws_id) {
          params.pws_id = this.pws_id;
        }
        if (this.responseStatusFilter) {
          params.status = this.responseStatusFilter;
        }
        const response = await axios.get('/api/sigdefs/responses', { params });
        this.myResponses = response.data;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading responses:', error);
        this.errorMessage = 'Failed to load responses';
      } finally {
        this.loadingResponses = false;
      }
    },
    async loadNotificationCounts() {
      try {
        const params = {};
        if (this.pws_id) {
          params.pws_id = this.pws_id;
        }
        const response = await axios.get('/api/sigdefs/notification-count', { params });
        this.notificationCounts = response.data;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading notification counts:', error);
      }
    },
    async viewDeficiencyDetail(sigdefId) {
      try {
        // Reset preview data when loading new detail
        this.previewUrl = null;
        this.previewType = null;
        this.previewTitle = '';

        const response = await axios.get(`/api/sigdefs/${sigdefId}`);
        this.detailDeficiency = response.data;
        // Use nextTick to ensure detailDeficiency is set before changing tab
        this.$nextTick(() => {
          this.activeTab = 3; // Switch to detail tab
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading deficiency detail:', error);
        this.errorMessage = 'Failed to load deficiency details';
      }
    },
    // A response can only be edited in place until a reviewer has acted on it.
    // 'needs_clarification' is a reviewer action, so it is NOT editable: the
    // system answers a clarification request by filing a new response, which
    // keeps the request/answer thread intact instead of overwriting what was
    // said before.
    isResponseEditable(status) {
      return status === 'pending';
    },
    // Once a response has been reviewed, further documentation is filed as a NEW
    // response rather than an edit -- but only where the system still owes us
    // something. 'needs_clarification' is the common case: MSDH asked a question,
    // and the answer belongs in the thread as its own response.
    // 'accepted' is excluded deliberately: MSDH is finished with the
    // deficiency and is just waiting for the resolution date to be entered in
    // SDWIS, so inviting more submissions there is confusing, and it would let a
    // deficiency accumulate two 'accepted' responses (see
    // server/docs/SIGDEF_FOLLOWUP_NOTES.md -- that breaks the SDWIS MTS resolve
    // generator, which is one-row-per-accepted-response).
    canSubmitFollowUp(status) {
      return status === 'needs_clarification'
        || status === 'active_schedule'
        || status === 'rejected';
    },
    // Shared by the list cards and the Detail View so the two can't drift.
    // A null status means the deficiency has no response at all yet.
    followUpButtonLabel(status) {
      if (!status) return 'Submit Response';
      if (status === 'needs_clarification') return 'Respond to Clarification Request';
      if (status === 'rejected') return 'Submit Corrected Response';
      return 'Submit Documentation / Extension Request';
    },
    // Latest response on the deficiency currently open in the Detail View
    detailLatestStatus() {
      const responses = this.detailDeficiency && this.detailDeficiency.responses;
      if (!responses || responses.length === 0) return null;
      return responses[0].status;
    },
    async showResponseForm(deficiency, isEdit = false) {
      this.selectedDeficiency = deficiency;

      if (isEdit && deficiency.response_id) {
        try {
          // Fetch full response details
          const response = await axios.get(`/api/sigdefs/${deficiency.sigdef_id}`);
          const fullDeficiency = response.data;

          if (fullDeficiency.responses && fullDeficiency.responses.length > 0) {
            // Find the response (should be the most recent one)
            const responseData = fullDeficiency.responses[0];
            this.editingResponse = {
              id: responseData.id,
              response_text: responseData.response_text || '',
              resolution_date: responseData.resolution_date || '',
              files: responseData.files || [],
            };
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error loading response for editing:', error);
          this.errorMessage = 'Failed to load response data';
          return;
        }
      } else {
        this.editingResponse = null;
      }

      // Use nextTick to ensure selectedDeficiency is set before changing tab
      this.$nextTick(() => {
        this.activeTab = 2; // Switch to response form tab
      });
    },
    clearSelection() {
      this.selectedDeficiency = null;
      this.editingResponse = null;
      this.activeTab = 0;
    },
    closeDetail() {
      this.detailDeficiency = null;
      this.previewUrl = null;
      this.previewType = null;
      this.previewTitle = '';
      this.activeTab = 0;
    },
    onResponseSubmitted(payload) {
      // Refresh lists
      this.loadUnresolvedDeficiencies();
      this.loadMyResponses();
      this.loadNotificationCounts();

      // If the submission was started from the detail view, reload it so the
      // new response shows up in the history and stay on that tab
      const sigdefId = payload && payload.sigdef_id;
      if (this.detailDeficiency && sigdefId === this.detailDeficiency.sigdef_id) {
        this.selectedDeficiency = null;
        this.editingResponse = null;
        this.viewDeficiencyDetail(sigdefId);
        return;
      }

      this.clearSelection();
      this.activeTab = 1; // Switch to My Responses tab
    },
    // File operations
    downloadFile(fileId) {
      window.open(`/api/files/${fileId}/download`, '_blank');
    },
    handleDeleteFile(file) {
      this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete ${file.filename}?`,
        {
          title: 'Confirm Delete',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
        },
      ).then((confirmed) => {
        if (confirmed) {
          this.performDeleteFile(file);
        }
      });
    },
    async performDeleteFile(file) {
      try {
        await axios.delete(`/api/files/${file.id}`);
        // Remove from editingResponse.files array
        if (this.editingResponse && this.editingResponse.files) {
          this.editingResponse.files = this.editingResponse.files.filter((f) => f.id !== file.id);
        }
        this.$bvToast.toast('File deleted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting file:', error);
        this.$bvToast.toast('Failed to delete file', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      }
    },
    // Formatting helpers
    // Keep utility methods for backward compatibility with tests
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / (k ** i)).toFixed(1))} ${sizes[i]}`;
    },
    getFileIcon(file) {
      const filename = file.filename || file.name;
      const ext = filename.split('.').pop().toLowerCase();
      switch (ext) {
        case 'pdf':
          return 'file-earmark-pdf';
        case 'jpg':
        case 'jpeg':
        case 'png':
          return 'file-earmark-image';
        case 'docx':
        case 'doc':
          return 'file-earmark-word';
        default:
          return 'file-earmark';
      }
    },
    canPreview(file) {
      const mimeType = file.mime_type || '';
      return mimeType === 'application/pdf' || mimeType.startsWith('image/');
    },
    formatStatus(status) {
      const statusMap = {
        pending: 'Pending',
        under_review: 'Under Review',
        accepted: 'Accepted',
        active_schedule: 'Active Schedule',
        rejected: 'Rejected',
        needs_clarification: 'Needs Clarification',
      };
      return statusMap[status] || status;
    },
    getStatusVariant(status) {
      const variantMap = {
        pending: 'warning',
        under_review: 'info',
        accepted: 'success',
        active_schedule: 'info',
        rejected: 'danger',
        needs_clarification: 'warning',
      };
      return variantMap[status] || 'secondary';
    },
    getActivityStatusVariant(status) {
      const variantMap = {
        Completed: 'success',
        Overdue: 'danger',
        Pending: 'warning',
      };
      return variantMap[status] || 'secondary';
    },
    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return `${text.substring(0, maxLength)}...`;
    },
    previewFile(file) {
      const mimeType = file.mime_type || '';

      // Check if mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      // On mobile, for PDFs, just download instead of preview
      if (isMobile && mimeType === 'application/pdf') {
        window.open(`/api/files/${file.id}/download`, '_blank');
        return;
      }

      // Clear previous preview to avoid race conditions
      this.previewUrl = null;
      this.previewType = null;

      // Set preview metadata
      this.previewTitle = file.filename;

      if (mimeType === 'application/pdf') {
        this.previewType = 'pdf';
      } else if (mimeType.startsWith('image/')) {
        this.previewType = 'image';
      }

      // Set URL and show modal
      this.previewUrl = `/api/files/${file.id}/preview`;
      this.$bvModal.show('file-preview-modal');
    },
  },
};
</script>

<style scoped>
.sigdef-responses {
  padding: 20px;
}

/* MSDH's own words, wherever they appear: lifted off the tinted alert so they
   read as a quoted message rather than as a caption on it. Matches the
   clarification callout in SigDefResponseForm. */
.reviewer-note {
  background-color: #fff;
  border-left: 4px solid #17a2b8;
  border-radius: 0.25rem;
  padding: 0.75rem 1rem;
}

.reviewer-note-label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #0c5460;
  margin-bottom: 0.35rem;
}

.reviewer-note-text {
  /* Reviewer notes are frequently numbered lists -- keep the typed breaks. */
  white-space: pre-wrap;
  line-height: 1.5;
  color: #212529;
}
</style>
